import React from 'react';
import { FaTimes, FaHeartbeat, FaPrint, FaExclamationTriangle, FaCalendarAlt, FaShieldAlt } from 'react-icons/fa';

const MonitoringModal = ({ medication, onClose }) => {
  const riskConfig = {
    'RED': {
      bg: 'bg-neo-danger',
      light: 'bg-neo-danger',
      border: 'border-neo-main',
      text: 'text-neo-main',
      badge: 'bg-white text-neo-danger',
    },
    'YELLOW': {
      bg: 'bg-neo-warning',
      light: 'bg-neo-warning',
      border: 'border-neo-main',
      text: 'text-neo-main',
      badge: 'bg-white text-neo-warning',
    },
    'GREEN': {
      bg: 'bg-neo-success',
      light: 'bg-neo-success',
      border: 'border-neo-main',
      text: 'text-neo-main',
      badge: 'bg-white text-neo-success',
    },
  };

  const config = riskConfig[medication.risk_category] || riskConfig['GREEN'];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 modal-overlay">
      <div className="bg-neo-card rounded-lg border-2 border-neo-main shadow-neo-brutal max-w-2xl w-full max-h-[90vh] overflow-hidden modal-content flex flex-col">
        {/* Header */}
        <div className={`${config.bg} text-neo-main p-6 border-b-2 border-neo-main relative overflow-hidden flex-shrink-0`}>
          <div className="relative flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white text-neo-main rounded-md flex items-center justify-center border-2 border-neo-main shadow-neo-sm">
                <FaHeartbeat className="text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-black">Monitoring Plan</h2>
                <p className="font-bold opacity-90 mt-1">{medication.name}</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="w-10 h-10 bg-white text-neo-main hover:bg-neo-bg rounded-md flex items-center justify-center 
                       transition-all duration-200 border-2 border-neo-main shadow-neo-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
            >
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar space-y-6 animate-fade-in">
          <div className={`bg-white p-5 rounded-lg border-2 border-neo-main shadow-neo-sm`}>
            <h3 className="text-lg font-black text-neo-main mb-4 border-b-2 border-neo-main inline-block">Risk Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-neo-card p-4 rounded-md border-2 border-neo-main shadow-neo-sm">
                <p className="text-sm font-black text-neo-text-sub uppercase tracking-wide mb-1">Risk Category</p>
                <p className={`text-2xl font-black ${config.text} bg-white inline-block px-2 rounded border-2 border-neo-main`}>
                  {medication.risk_category}
                </p>
              </div>
              <div className="bg-neo-card p-4 rounded-md border-2 border-neo-main shadow-neo-sm">
                <p className="text-sm font-black text-neo-text-sub uppercase tracking-wide mb-1">Risk Score</p>
                <p className="text-2xl font-black text-neo-main">
                  {medication.risk_score}<span className="text-lg text-neo-text-sub font-bold">/10</span>
                </p>
              </div>
            </div>
          </div>

          {medication.monitoring_required?.length > 0 && (
            <div className="bg-neo-bg p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
              <h3 className="text-lg font-black text-neo-main mb-4 flex items-center gap-2">
                <FaShieldAlt className="text-neo-accent" />
                Monitoring Parameters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {medication.monitoring_required.map((param, index) => (
                  <div 
                    key={index} 
                    className="bg-white p-4 rounded-md border-2 border-neo-main shadow-neo-pressed
                             hover:shadow-neo-sm transition-all duration-200"
                  >
                    <p className="font-bold text-neo-main">{param}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {medication.flags?.length > 0 && (
            <div className="bg-neo-warning text-neo-main p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
              <h3 className="text-lg font-black mb-4 flex items-center gap-2">
                <FaExclamationTriangle className="text-white drop-shadow-sm" />
                Identified Issues
              </h3>
              <ul className="space-y-2">
                {medication.flags.map((flag, index) => (
                  <li key={index} className="flex items-start gap-3 font-bold bg-white p-3 rounded-md border-2 border-neo-main shadow-neo-pressed">
                    <FaExclamationTriangle className="text-neo-warning mt-0.5 flex-shrink-0" />
                    <span>{flag}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-neo-card p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
            <h3 className="text-lg font-black text-neo-main mb-4 flex items-center gap-2">
              <FaCalendarAlt className="text-neo-accent" />
              Recommended Monitoring Schedule
            </h3>
            <div className="space-y-3">
              {medication.risk_category === 'RED' && (
                <>
                  {[
                    { period: 'Week 1-2', schedule: 'Daily to every other day assessment', color: 'neo-danger' },
                    { period: 'Week 3-4', schedule: 'Twice weekly assessment', color: 'neo-warning' },
                    { period: 'Week 5+', schedule: 'Weekly until stable', color: 'neo-success' },
                  ].map((item, i) => (
                    <div key={i} className={`bg-white p-4 rounded-md border-2 border-neo-main shadow-neo-pressed`}>
                      <p className={`font-black text-neo-main mb-1 inline-block px-2 py-0.5 rounded border-2 border-neo-main bg-${item.color} ${item.color === 'neo-danger' || item.color === 'neo-success' ? 'text-neo-main' : 'text-neo-main'}`}>{item.period}</p>
                      <p className="text-neo-text font-medium">{item.schedule}</p>
                    </div>
                  ))}
                </>
              )}
              {medication.risk_category === 'YELLOW' && (
                <>
                  {[
                    { period: 'First Month', schedule: 'Bi-weekly assessment', color: 'neo-warning' },
                    { period: 'Ongoing', schedule: 'Monthly review', color: 'neo-accent' },
                  ].map((item, i) => (
                    <div key={i} className="bg-white p-4 rounded-md border-2 border-neo-main shadow-neo-pressed">
                      <p className={`font-black text-neo-main mb-1 inline-block px-2 py-0.5 rounded border-2 border-neo-main bg-${item.color} ${item.color === 'neo-accent' ? 'text-white' : 'text-neo-main'}`}>{item.period}</p>
                      <p className="text-neo-text font-medium">{item.schedule}</p>
                    </div>
                  ))}
                </>
              )}
              {medication.risk_category === 'GREEN' && (
                <div className="bg-white p-4 rounded-md border-2 border-neo-main shadow-neo-pressed">
                  <p className="font-black text-neo-main bg-neo-success inline-block px-2 py-0.5 rounded border-2 border-neo-main mb-1">Routine Monitoring</p>
                  <p className="text-neo-text font-medium">Every 3-6 months or as clinically indicated</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-neo-danger text-neo-main p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
            <h3 className="text-lg font-black mb-4 flex items-center gap-2">
              <FaExclamationTriangle className="text-white drop-shadow-sm" />
              Safety Instructions
            </h3>
            <ul className="space-y-2">
              {[
                'Contact healthcare provider immediately if concerning symptoms develop',
                'Keep a symptom diary for tracking changes',
                'Do not adjust doses without medical supervision',
                'Report any new medications or supplements started',
                ...(medication.risk_category === 'RED' 
                  ? ['This is a HIGH PRIORITY medication - enhanced vigilance required'] 
                  : [])
              ].map((instruction, index) => (
                <li 
                  key={index} 
                  className={`flex items-start gap-3 bg-white text-neo-danger p-3 rounded-md border-2 border-neo-main shadow-neo-pressed
                            ${index === 4 ? 'font-black border-4' : 'font-bold'}`}
                >
                  <span className="text-neo-danger mt-0.5">&#9888;</span>
                  <span>{instruction}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-white p-4 flex justify-end gap-3 border-t-2 border-neo-main flex-shrink-0">
          <button
            onClick={() => window.print()}
            className="px-5 py-2.5 bg-neo-accent text-white border-2 border-neo-main rounded-md 
                     font-bold hover:bg-blue-600 transition-all duration-200
                     flex items-center gap-2 shadow-neo-brutal hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
          >
            <FaPrint />
            Print Plan
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-neo-main text-white border-2 border-neo-main rounded-md font-bold 
                     hover:bg-gray-800 transition-all duration-200 shadow-neo-brutal hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonitoringModal;
